import { Component, Input, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltip, MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-table-copy-cell',
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatIconModule, MatTooltipModule, TranslateModule],
  templateUrl: './table-copy-cell.component.html',
  styleUrls: ['./table-copy-cell.component.scss'],
})
export class AvTableCopyCellComponent {
  private readonly truncationLimit = 26;
  private readonly _translate = inject(TranslateService);

  @Input() content: string = '';
  @Input() value?: string;
  @Input() tooltipText: string = '';

  copyFeedbackTooltip = signal<string | null>(null);

  effectiveValue(): string {
    return this.value ?? this.content ?? '';
  }

  isTruncated(): boolean {
    return (this.content ?? '').length > this.truncationLimit;
  }

  copyContentText(): string {
    return this._translate.instant('shared.general.labels.copy');
  }

  copiedText(): string {
    return this._translate.instant('shared.general.messages.copied_to_clipboard');
  }

  naLabel(): string {
    return this._translate.instant('shared.general.labels.na');
  }

  displayContent(): string {
    const content = this.content ?? '';
    return this.isTruncated() ? `${content.slice(0, this.truncationLimit)}...` : content;
  }

  tooltipContent(): string {
    const feedback = this.copyFeedbackTooltip();
    if (feedback) return feedback;
    if (this.isTruncated()) return this.content;
    return this.copyContentText() + " " + this.tooltipText;
  }

  iconTooltipContent(): string {
    const feedback = this.copyFeedbackTooltip();
    if (feedback) return feedback;
    return this.copyContentText() + " " + this.tooltipText;
  }

  copyToClipboard(tooltip: MatTooltip): void {
    navigator.clipboard.writeText(this.effectiveValue()).then(() => {
      this.copyFeedbackTooltip.set(this.copiedText());
      tooltip.show();

      setTimeout(() => {
        this.copyFeedbackTooltip.set(null);
      }, 5000);
    }).catch(err => {
      console.error('Failed to copy: ', err);
    });
  }
}
